import { memo } from "react";
import { Link } from "@remix-run/react";
import { Button, Image, useDisclosure } from "@heroui/react";
import { FaArrowDown } from "react-icons/fa";
import { ProductCard } from "../Product/ProductCard";
import { useSetAtom } from "jotai";
import { productPreviewAtom } from "app/atoms/product.atom";
import allCategories from "app/utils/categories";
import { toKebabCase } from "app/utils/fn";
import { FaFolder } from "react-icons/fa6";
import { category } from "app/api/category/category.types";
import { categoryIcons } from "app/utils/categoryIcon";

interface ProductSectionProps {
  title?: string;
  showmore?: boolean;
  heroImage: string;
  category: category;
  products?: any[];
}

interface IsubCategory {
  _id: string;
  name: string;
  category: string;
  id: string;
}

const ProductSection = ({
  products,
  showmore,
  title,
  heroImage,
  category,
}: ProductSectionProps) => {
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const setProduct = useSetAtom(productPreviewAtom);
  const IconComponent = categoryIcons[category.name] || FaFolder;

  // Get sub-categories from a static list.
  const subCategories =
    (allCategories.find((cat) => cat.name === category.name)?.subCategory as IsubCategory[]) || [];

  return (
    <section className="relative xl:min-h-[30rem]" aria-labelledby="product-section-title">
      {/* Mobile Hero Banner */}
      <div className="relative w-full md:hidden h-40" role="banner">
        <Image
          src={heroImage}
          alt={`Hero image for ${title}`}
          removeWrapper
          radius="none"
          className="h-full w-full transition object-cover"
          loading="lazy"
          width="100%"
          height="160"
        />
        <div className="bg-primary h-full w-full" aria-hidden="true" />
        <div className="absolute inset-0 z-20">
          <div className="flex flex-col gap-2 h-full justify-center items-center bg-primary/50">
            <div className="w-14 h-14">
              <IconComponent
                className="text-white h-full w-full transition"
                aria-label={`${title} icon`}
              />
            </div>
            <div className="relative flex justify-center bg-black/5 w-full">
              <span className="text-white text-xl">{title}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop View */}
      <div className="bg-white-bg dark:bg-gray-800 px-3 md:px-6 lg:px-8 xl:px-12">
        <div className="container mx-auto flex flex-col items-center">
          <div className="flex flex-col w-full md:grid md:grid-cols-[3fr_9fr] md:gap-4 md:py-10">
            {/* Category Sidebar */}
            <aside
              className="hidden md:block relative rounded-lg overflow-hidden min-h-[50rem] md:min-w-[16rem] md:max-w-[17rem] 2xl:max-w-[20rem] flex-grow"
              aria-label={`Category navigation for ${title}`}
            >
              <Image
                src={heroImage}
                alt={`Hero image for ${title}`}
                removeWrapper
                radius="none"
                className="absolute inset-0 h-full w-full transition object-cover bg-primary"
                loading="lazy"
                width="400"
                height="800"
              />
              <div className="bg-primary h-full w-full" aria-hidden="true" />
              <div className="absolute bg-primary/70 inset-0 z-20 p-4 md:p-6">
                <div className="w-14 h-14 mb-4">
                  <IconComponent
                    className="text-white h-full w-full transition"
                    aria-label={`${title} icon`}
                  />
                </div>
                <h2 id={title} className="text-white text-2xl mb-4">
                  {title}
                </h2>
                <nav aria-label="Sub-categories">
                  <ul className="space-y-2">
                    {subCategories.slice(0, 15).map((cat) => (
                      <li key={cat._id}>
                        <Link
                          className="text-white focus:outline-none focus:ring-2 focus:ring-offset-2"
                          to={`/category/${toKebabCase(category.name)}/${toKebabCase(cat.name)}`}
                        >
                          {cat.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </nav>
                {category?.name && (
                  <div className="mt-12">
                    <Button
                      as={Link}
                      to={`/category/${toKebabCase(category.name)}`}
                      className="bg-white-bg p-3 rounded-md text-primary text-base hover:opacity-80 transition text-center capitalize"
                      size="md"
                      variant="flat"
                      fullWidth
                      aria-label="View full collection"
                    >
                      View Collection
                    </Button>
                  </div>
                )}
              </div>
            </aside>

            {/* Products Grid */}
            <div
              className="grid grid-cols-2 lg:grid-cols-3 gap-1 gap-y-8 md:gap-4 2xl:gap-5 py-3 lg:py-0 "
              aria-label="Product list"
            >
              {products &&
                products.length > 0 &&
                products
                  .slice(0, 9)
                  .map((item: any) => (
                    <ProductCard
                      key={item._id || item.id}
                      labels={item?.labels || []}
                      image={item?.overview?.heroImage}
                      images={item?.product?.images}
                      title={item?.overview?.name}
                      productCode={item?.overview?.code}
                      isSpecialSupplier={item?.supplier?.isSpecialSupplier}
                      description={item?.product?.description}
                      userTypePrice={item?.userTypePrice}
                      qty={item?.quantity}
                      slug={item?.slug}
                      category={category.name}
                      subCatgory={
                        item?.subCategory?.name ||
                        item?.product?.categorisation?.supplierSubCategory
                      }
                    />
                  ))}
            </div>
          </div>
        </div>
        {showmore && (
          <div className="md:hidden mx-auto w-fit py-2">
            <Button
              as={Link}
              to={`/category/${toKebabCase(category.name)}`}
              variant="solid"
              radius="md"
              size="sm"
              color="primary"
              startContent={<FaArrowDown className="sm:text-2xl lg:text-xl" />}
              className="bg-primary px-5 py-6 text-white-bg gap-5 text-base font-semibold hover:opacity-80 transition text-center capitalize mx-auto"
              aria-label="View full collection"
            >
              View Collection
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default memo(ProductSection);
